import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Calendar, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  User, DollarSign, FileText, Check, X, Pencil
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ACTION_COLORS = {
  Completed: "text-[#6B8F62]",
  Hold: "text-[#7A6535]",
  Returned: "text-[#C07070]",
  "Not Done": "text-gray-400",
  "Transmitted Next Day": "text-[#3B7A9C]",
};

const PAY_OPTIONS = [
  { value: "", label: "Not Set", cls: "bg-gray-100 text-gray-400 border-gray-200" },
  { value: "Full Day", label: "Full Day", cls: "bg-[#6B8F62]/10 text-[#6B8F62] border-[#6B8F62]/25" },
  { value: "Per Order", label: "Per Order", cls: "bg-[#2F5D8A]/10 text-[#2F5D8A] border-[#2F5D8A]/25" },
];

const PayBadge = ({ type }) => {
  const opt = PAY_OPTIONS.find(o => o.value === type) || PAY_OPTIONS[0];
  return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${opt.cls}`}>{opt.label}</span>;
};

const ActionBadge = ({ action }) => {
  const colors = {
    Completed: "bg-[#6B8F62]/10 text-[#6B8F62] border-[#6B8F62]/25",
    Hold: "bg-[#D1A054]/10 text-[#7A6535] border-[#D1A054]/30",
    Returned: "bg-[#C07070]/10 text-[#C07070] border-[#C07070]/25",
    "Not Done": "bg-gray-100 text-gray-500 border-gray-300",
    "Transmitted Next Day": "bg-[#3B7A9C]/10 text-[#3B7A9C] border-[#3B7A9C]/25",
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${colors[action] || colors["Not Done"]}`}>{action}</span>;
};

const WeeklyClosingSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [expandedInsp, setExpandedInsp] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [editingPay, setEditingPay] = useState(null);
  const [payForm, setPayForm] = useState({ payment_type: "", notes: "", final_count: 0 });
  const [dayOffMap, setDayOffMap] = useState({}); // { inspector: { date: record } }
  const [dayOffModal, setDayOffModal] = useState(null); // record currently viewed

  const getWeekStart = useCallback(() => {
    const now = new Date();
    const daysSinceTue = (now.getDay() - 2 + 7) % 7;
    const tue = new Date(now);
    tue.setDate(now.getDate() - daysSinceTue + (weekOffset * 7));
    return `${tue.getFullYear()}-${String(tue.getMonth() + 1).padStart(2, "0")}-${String(tue.getDate()).padStart(2, "0")}`;
  }, [weekOffset]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const ws = getWeekStart();
      const res = await axios.get(`${API}/weekly-closing`, { params: { week_start: ws } });
      setData(res.data);
      // Fetch bulk day-offs for this week (7 days)
      try {
        const startD = new Date(ws + "T00:00:00");
        const endD = new Date(startD); endD.setDate(endD.getDate() + 6);
        const iso = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
        const doRes = await axios.get(`${API}/inspector/day-offs-bulk`, { params: { start: iso(startD), end: iso(endD) } });
        const map = {};
        (doRes.data.records || []).forEach(r => {
          if (!map[r.inspector_name]) map[r.inspector_name] = {};
          map[r.inspector_name][r.day_off_date] = r;
        });
        setDayOffMap(map);
      } catch { setDayOffMap({}); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [getWeekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEditPay = (inspName, day) => {
    setEditingPay({ inspector_name: inspName, route_date: day.date });
    setPayForm({
      payment_type: day.payment_type || "",
      notes: day.payment_notes || "",
      final_count: day.final_count || day.totals.total,
    });
  };

  const savePay = async () => {
    if (!editingPay || !data) return;
    try {
      await axios.patch(`${API}/weekly-closing/pay-decision`, {
        inspector_name: editingPay.inspector_name,
        route_date: editingPay.route_date,
        week_start: data.week_start,
        payment_type: payForm.payment_type,
        notes: payForm.notes,
        final_count: payForm.final_count,
      });
      setEditingPay(null);
      fetchData();
    } catch (e) { console.error(e); }
  };

  const formatDateShort = (ds) => {
    if (!ds) return "";
    try {
      const d = new Date(ds + "T00:00:00");
      return `${d.getMonth() + 1}/${d.getDate()}`;
    } catch { return ds; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#B08A3E]" />
      </div>
    );
  }

  if (!data) return null;

  const { dates, day_labels, inspectors, week_start, week_end } = data;

  return (
    <div className="space-y-4" data-testid="weekly-closing-section">
      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-[#B08A3E]" />
          <h2 className="text-base font-bold text-[#1F2937]">Weekly Closing</h2>
          <span className="text-xs text-gray-400">Pay Control Calendar</span>
        </div>
        <div className="flex items-center gap-2" data-testid="week-nav">
          <button onClick={() => setWeekOffset(o => o - 1)} className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600" data-testid="week-prev">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium text-[#1F2937] min-w-[180px] text-center">
            {formatDateShort(week_start)} — {formatDateShort(week_end)}
          </span>
          <button onClick={() => setWeekOffset(o => o + 1)} className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600" data-testid="week-next">
            <ChevronRight className="h-4 w-4" />
          </button>
          {weekOffset !== 0 && (
            <button onClick={() => setWeekOffset(0)} className="px-2 py-1 text-[10px] text-[#2F5D8A] hover:bg-[#2F5D8A]/5 rounded font-medium">This Week</button>
          )}
        </div>
      </div>

      {/* Inspectors */}
      {inspectors.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100" data-testid="weekly-empty">
          <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-200" />
          <p className="text-sm text-gray-400">No closing records for this week</p>
        </div>
      ) : (
        <div className="space-y-3">
          {inspectors.map(insp => {
            const isExpanded = expandedInsp === insp.inspector_name;
            return (
              <div key={insp.inspector_name} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`weekly-inspector-${insp.inspector_name}`}>
                {/* Inspector Header */}
                <button onClick={() => setExpandedInsp(isExpanded ? null : insp.inspector_name)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-[#B08A3E]" />
                    <span className="font-semibold text-[#1F2937] text-sm">{insp.inspector_name}</span>
                    <span className="text-xs text-gray-400">{insp.week_total} orders this week</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Mini day indicators */}
                    {insp.days.map((day, i) => {
                      const off = dayOffMap[insp.inspector_name]?.[day.date];
                      if (off) {
                        return (
                          <div
                            key={i}
                            role="button"
                            onClick={(e) => { e.stopPropagation(); setDayOffModal(off); }}
                            className="w-6 h-6 rounded flex items-center justify-center text-[8px] font-extrabold bg-[#C07070]/15 text-[#8B4545] border border-[#C07070]/40 cursor-pointer hover:bg-[#C07070]/25"
                            title={`${day_labels[i]} — DAY OFF${off.comment ? `: ${off.comment}` : ""}`}
                            data-testid={`weekly-mini-off-${insp.inspector_name}-${day.date}`}
                          >
                            OFF
                          </div>
                        );
                      }
                      return (
                        <div key={i} className={`w-6 h-6 rounded flex items-center justify-center text-[9px] font-bold ${
                          day.totals.total > 0
                            ? day.payment_type === "Full Day" ? "bg-[#6B8F62]/15 text-[#6B8F62]"
                              : day.payment_type === "Per Order" ? "bg-[#2F5D8A]/10 text-[#2F5D8A]"
                              : "bg-gray-100 text-[#1F2937]"
                            : "bg-gray-50 text-gray-300"
                        }`} title={`${day_labels[i]}: ${day.totals.total} orders`}>
                          {day.totals.total || "-"}
                        </div>
                      );
                    })}
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400 ml-1" /> : <ChevronDown className="h-4 w-4 text-gray-400 ml-1" />}
                  </div>
                </button>

                {/* Expanded Week View */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {/* Day Headers */}
                    <div className="grid grid-cols-7 gap-px bg-gray-100">
                      {insp.days.map((day, i) => {
                        const isDayExpanded = expandedDay === `${insp.inspector_name}_${day.date}`;
                        const isEditingThis = editingPay?.inspector_name === insp.inspector_name && editingPay?.route_date === day.date;
                        const hasOrders = day.totals.total > 0;
                        const off = dayOffMap[insp.inspector_name]?.[day.date];

                        if (off) {
                          return (
                            <div key={i} className="bg-[#C07070]/[0.05]">
                              <button
                                onClick={(e) => { e.stopPropagation(); setDayOffModal(off); }}
                                className="w-full px-2 py-2 text-left hover:bg-[#C07070]/10 cursor-pointer transition-colors"
                                data-testid={`weekly-day-off-${insp.inspector_name}-${day.date}`}
                                title="View Day Off details"
                              >
                                <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">{day_labels[i]}</p>
                                <p className="text-[10px] text-gray-500">{formatDateShort(day.date)}</p>
                                <div className="mt-1">
                                  <span className="inline-block px-1.5 py-0.5 rounded bg-[#C07070]/20 text-[#8B4545] text-[10px] font-extrabold tracking-wider border border-[#C07070]/30">
                                    OFF
                                  </span>
                                </div>
                                {off.comment && (
                                  <p className="text-[9px] text-[#8B4545] mt-1 line-clamp-2 italic">"{off.comment}"</p>
                                )}
                              </button>
                            </div>
                          );
                        }

                        return (
                          <div key={i} className={`bg-white ${hasOrders ? '' : 'opacity-60'}`}>
                            {/* Day Header */}
                            <button onClick={() => hasOrders && setExpandedDay(isDayExpanded ? null : `${insp.inspector_name}_${day.date}`)}
                              className={`w-full px-2 py-2 text-left ${hasOrders ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
                              data-testid={`day-${insp.inspector_name}-${day.date}`}
                            >
                              <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">{day_labels[i]}</p>
                              <p className="text-[10px] text-gray-500">{formatDateShort(day.date)}</p>
                              <p className={`text-lg font-bold mt-0.5 leading-none ${hasOrders ? 'text-[#1F2937]' : 'text-gray-300'}`}>{day.totals.total}</p>
                              {hasOrders && (
                                <div className="flex flex-wrap gap-x-2 mt-1">
                                  {day.totals.Completed > 0 && <span className="text-[9px] text-[#6B8F62]">{day.totals.Completed} done</span>}
                                  {day.totals.Hold > 0 && <span className="text-[9px] text-[#7A6535]">{day.totals.Hold} hold</span>}
                                  {day.totals.Returned > 0 && <span className="text-[9px] text-[#C07070]">{day.totals.Returned} ret</span>}
                                  {day.totals["Not Done"] > 0 && <span className="text-[9px] text-gray-400">{day.totals["Not Done"]} nd</span>}
                                </div>
                              )}
                              {/* Payment badge */}
                              {day.payment_type && (
                                <div className="mt-1.5">
                                  <PayBadge type={day.payment_type} />
                                </div>
                              )}
                            </button>

                            {/* Expanded Day Details */}
                            {isDayExpanded && hasOrders && (
                              <div className="border-t border-gray-100 px-2 py-2 space-y-2 bg-gray-50/50">
                                {/* Payment Control */}
                                <div className="bg-white rounded-lg border border-gray-200 p-2">
                                  {isEditingThis ? (
                                    <div className="space-y-1.5">
                                      <div>
                                        <label className="text-[8px] text-gray-400 uppercase font-medium">Payment Type</label>
                                        <select value={payForm.payment_type} onChange={e => setPayForm(p => ({...p, payment_type: e.target.value}))}
                                          className="w-full px-1.5 py-1 bg-gray-50 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#B08A3E]"
                                          data-testid="pay-type-select"
                                        >
                                          <option value="">Not Set</option>
                                          <option value="Full Day">Full Day</option>
                                          <option value="Per Order">Per Order</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="text-[8px] text-gray-400 uppercase font-medium">Final Count</label>
                                        <input type="number" value={payForm.final_count} onChange={e => setPayForm(p => ({...p, final_count: parseInt(e.target.value) || 0}))}
                                          className="w-full px-1.5 py-1 bg-gray-50 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#B08A3E]"
                                          data-testid="pay-final-count"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-[8px] text-gray-400 uppercase font-medium">Notes</label>
                                        <input value={payForm.notes} onChange={e => setPayForm(p => ({...p, notes: e.target.value}))}
                                          placeholder="e.g. returned 2 orders, late start..."
                                          className="w-full px-1.5 py-1 bg-gray-50 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#B08A3E]"
                                          data-testid="pay-notes"
                                        />
                                      </div>
                                      <div className="flex gap-1">
                                        <button onClick={savePay} className="flex-1 px-2 py-1 bg-[#6B8F62] text-white text-[10px] rounded font-semibold" data-testid="pay-save">Save</button>
                                        <button onClick={() => setEditingPay(null)} className="flex-1 px-2 py-1 bg-gray-200 text-gray-600 text-[10px] rounded">Cancel</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-between">
                                      <div className="space-y-0.5">
                                        <div className="flex items-center gap-2">
                                          <DollarSign className="h-3 w-3 text-[#B08A3E]" />
                                          <PayBadge type={day.payment_type} />
                                          {day.final_count !== day.totals.total && day.payment_type && (
                                            <span className="text-[9px] text-gray-500">Count: {day.final_count}</span>
                                          )}
                                        </div>
                                        {day.payment_notes && <p className="text-[9px] text-gray-400 italic">{day.payment_notes}</p>}
                                      </div>
                                      <button onClick={() => startEditPay(insp.inspector_name, day)}
                                        className="p-1 rounded text-gray-300 hover:text-[#B08A3E] hover:bg-[#B08A3E]/10"
                                        data-testid={`edit-pay-${day.date}`}
                                      >
                                        <Pencil className="h-3 w-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* Orders List */}
                                <div className="space-y-0.5 max-h-48 overflow-y-auto">
                                  {day.orders.map((o, idx) => (
                                    <div key={idx} className="flex items-center gap-2 px-1.5 py-1 text-[10px] bg-white rounded border border-gray-100">
                                      <span className="font-medium text-[#1F2937] w-16 truncate">{o.order_number}</span>
                                      <span className="text-gray-400 w-16 truncate">{o.company}</span>
                                      <span className="text-gray-400 flex-1 truncate">{o.address}, {o.city}</span>
                                      <ActionBadge action={o.action} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Day Off Detail Modal */}
      {dayOffModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="weekly-day-off-modal" onClick={() => setDayOffModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 bg-gradient-to-r from-[#C07070]/10 to-[#D1A054]/10 border-b border-[#C07070]/20 flex items-center gap-2">
              <span className="px-2 py-0.5 rounded bg-[#C07070]/20 text-[#8B4545] text-[11px] font-extrabold tracking-wider border border-[#C07070]/30">OFF</span>
              <div className="flex-1">
                <h2 className="text-sm font-bold text-[#1F2937]">{dayOffModal.inspector_name}</h2>
                <p className="text-[11px] text-gray-500">
                  {dayOffModal.day_off_date}{dayOffModal.day_off_time ? ` · ${dayOffModal.day_off_time}` : ""}
                </p>
              </div>
              <button onClick={() => setDayOffModal(null)} className="p-1 text-gray-400 hover:text-gray-700" data-testid="weekly-day-off-modal-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {dayOffModal.comment ? (
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Comment</p>
                  <p className="text-sm text-[#1F2937] italic" data-testid="weekly-day-off-modal-comment">"{dayOffModal.comment}"</p>
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No comment provided</p>
              )}

              {dayOffModal.proof_url && (
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Proof Attachment</p>
                  {/\.(png|jpe?g|webp|gif|heic|heif)$/i.test(dayOffModal.proof_filename || dayOffModal.proof_url) ? (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${dayOffModal.proof_url}`} target="_blank" rel="noopener noreferrer" data-testid="weekly-day-off-modal-proof">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${dayOffModal.proof_url}`}
                        alt="Day off proof"
                        className="w-full max-h-[360px] object-contain rounded-lg border border-gray-200 bg-gray-50"
                      />
                    </a>
                  ) : (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${dayOffModal.proof_url}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#2F5D8A]/8 text-[#2F5D8A] rounded-lg text-xs font-medium hover:bg-[#2F5D8A]/15"
                      data-testid="weekly-day-off-modal-proof">
                      <FileText className="h-3.5 w-3.5" /> View {dayOffModal.proof_filename || "attachment"}
                    </a>
                  )}
                </div>
              )}

              <div className="pt-2 border-t border-gray-100">
                <p className="text-[10px] text-gray-400">
                  Recorded by <span className="font-semibold text-gray-600">{dayOffModal.created_by || "—"}</span>
                  {dayOffModal.created_at && ` · ${new Date(dayOffModal.created_at).toLocaleString()}`}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyClosingSection;
